import type {
  FollowButtonDisplay,
  FollowButtonOutline,
  FollowButtonSize,
  FollowButtonVariant,
  FollowType,
} from "@product/scmp-sdk";
import { FollowButtonAction, FollowButton as FollowButtonComponent } from "@product/scmp-sdk";
import { useAtomValue } from "jotai";
import qs from "qs";
import type { FunctionComponent } from "react";
import { useEffect, useState } from "react";

import { useLoginDialogStateHelper } from "~/components/login-dialog/hooks";
import { gdprAtom } from "~/components/tracking/gdpr";
import { sendGATracking } from "~/components/tracking/google-analytics/apis";
import type { FollowButtonSource } from "~/components/tracking/google-analytics/types";
import { accountAtom } from "~/lib/account";

import type { TooltipProps } from "./follow-tooltip";
import { FollowTooltip } from "./follow-tooltip";
import { useUserFollow } from "./hooks";
import type { FollowQueryStringPayload } from "./types";
import { computeCompletionAction } from "./utils";

export type Props = {
  className?: string;
  display?: FollowButtonDisplay;
  entityId: string;
  isDisabledTooltip?: boolean;
  isTooltipFlipDisabled?: boolean;
  name: string;
  outline?: FollowButtonOutline;
  size?: FollowButtonSize;
  source?: FollowButtonSource;
  tooltipProps?: TooltipProps;
  type: FollowType;
  variant?: FollowButtonVariant;
};

export const FollowButton: FunctionComponent<Props> = ({
  className,
  display,
  entityId,
  isDisabledTooltip = false,
  isTooltipFlipDisabled,
  name,
  outline,
  size,
  source,
  tooltipProps = {
    position: "bottom-start",
  },
  type,
  variant,
}) => {
  const { checkIsFollowed, handleFollowAction } = useUserFollow();
  const { isLoggedIn } = useAtomValue(accountAtom);
  const { openLoginDialog } = useLoginDialogStateHelper();

  const handleFollowClick = (type: FollowType, entityIds: string[], campaignName: string) => {
    if (isLoggedIn) {
      handleFollowAction(entityIds, type, FollowButtonAction.Follow, () => {
        sendGATracking({
          action: computeCompletionAction(FollowButtonAction.Follow, type, source),
          category: "Notification Center",
          label: location.href,
        });
      });
    } else {
      const queryStringPayload: FollowQueryStringPayload = {
        action: {
          follow: {
            [type]: entityIds,
          },
        },
        source,
      };
      const accountUrl = new URL(window.location.href);
      accountUrl.search = qs.stringify(queryStringPayload);
      openLoginDialog({
        description: `Log in or register to follow ${
          campaignName ? " " + `<span class="bold-text">${campaignName}</span>` + " " : ""
        }and get notified on newly published stories.`,
        destination: accountUrl.toString(),
        ga4CustomParameter: {
          follow_name: campaignName,
          follow_type: type,
          trigger_point: "follow",
        },
        title: "Get notified on your favourite topics",
      });
    }
  };

  const handleUnfollowClick = (type: FollowType, entityIds: string[], campaignName: string) => {
    handleFollowAction(entityIds, type, FollowButtonAction.Unfollow, () => {
      sendGATracking({
        action: computeCompletionAction(FollowButtonAction.Unfollow, type, source),
        category: "Notification Center",
        label: campaignName,
      });
    });
  };

  const [isFollowing, setIsFollowing] = useState(false);
  useEffect(() => {
    setIsFollowing(checkIsFollowed(type, entityId));
  }, [entityId, checkIsFollowed, type]);

  const handleClick = () => {
    if (isFollowing) {
      handleUnfollowClick(type, [entityId], name);
    } else {
      // Track click event for follow only when without source
      if (!source) {
        sendGATracking({
          action: `Notification Center/Follow/Click`,
          category: "Notification Center",
          label: location.href,
        });
      }
      handleFollowClick(type, [entityId], name);
    }
  };

  const { isAccepted } = useAtomValue(gdprAtom);

  return (
    <FollowTooltip
      {...tooltipProps}
      className={className}
      disabled={isFollowing || !isAccepted || isDisabledTooltip}
      isFlipDisabled={isTooltipFlipDisabled}
      name={name}
      type={type}
    >
      <FollowButtonComponent
        display={display}
        isFollowing={isFollowing}
        outline={outline}
        size={size}
        variant={variant}
        onClick={handleClick}
      />
    </FollowTooltip>
  );
};

FollowButton.displayName = "FollowButton";
